import { useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { Workspace } from "../../../types"
import { useWorkspaceService } from "../services/useWorkspaceService"
import useUserProfile from "../profile/useUserProfile"
import { StorageService } from "../../../services"
import { SELECTED_WORKSPACE_KEY } from "../../../constants"
import { getAvailableWorkspaces, resolveWorkspaceSelection } from "../../../utils/workspaceSelection"

type UseWorkspaceType = {
  workspace: Workspace | null
  isLoading: boolean
}

const useWorkspace = (): UseWorkspaceType => {

  const workspaceService = useWorkspaceService()
  const { profile } = useUserProfile()

  const { data: workspace, isLoading, error } = useQuery({
    queryKey: [SELECTED_WORKSPACE_KEY],
    queryFn: async (): Promise<Workspace | null> => {
      const availableWorkspaces = getAvailableWorkspaces(profile?.workspaces)
      if (!availableWorkspaces.length) {
        return null
      }

      const storedWorkspaceId = await StorageService.getStorageItem<string>(SELECTED_WORKSPACE_KEY)
      const workspaceId = resolveWorkspaceSelection({
        workspaces: availableWorkspaces,
        activeWorkspaces: profile?.active_workspace,
        storedWorkspaceId,
      })

      if (!workspaceId) {
        return null
      }

      if (storedWorkspaceId !== workspaceId) {
        await StorageService.setStorageItem(SELECTED_WORKSPACE_KEY, workspaceId)
      }

      try {
        return await workspaceService.getWorkspace(workspaceId)
      } catch (err) {
        // If backend fails for stale selection, return profile workspace to avoid blank UI.
        const fallbackWorkspace = availableWorkspaces.find(workspace => workspace?.id === workspaceId) ?? availableWorkspaces[0]
        return fallbackWorkspace ?? null
      }
    },
    select: (data: Workspace | null) => {
      const workspaceId = data?.id
      if (workspaceId && profile) {
        const workspace = profile.workspaces.find(w => w?.id === workspaceId) ?? profile.workspaces[0]
        if (workspace) {
          return { ...data, timezone: workspace.timezone, currency: workspace.currency }
        }
      }
      return data
    },
    enabled: Boolean((profile?.active_workspace ?? []).length || getAvailableWorkspaces(profile?.workspaces).length),
  })

  useEffect(() => {
    if (error) {
      // show error message to the user toast
    }
  }, [error])
  return { workspace: workspace ?? null, isLoading }
}

export default useWorkspace 
